import React from 'react';
import { BotVendor, ScanResult } from '../../utils/types';
import { formatDate } from '../../utils/date';
import { T as DT } from '../../designTokens';
import { T } from './tokens';

/** Display names for the anti-bot vendors the scanner classifies (#1339). */
const BOT_VENDOR_NAMES: Record<Exclude<BotVendor, 'unknown'>, string> = {
  cloudflare: 'Cloudflare',
  akamai: 'Akamai',
  datadome: 'DataDome',
  perimeterx: 'PerimeterX',
  imperva: 'Imperva',
  siteground: 'SiteGround',
  'aws-waf': 'AWS WAF',
};

/**
 * The vendor fronting the blocked homepage, from the page ledger, or
 * undefined when the scanner could not name one (#1869).
 */
function homepageBotVendor(result: ScanResult): string | undefined {
  const homepage = result.pageLedger?.visited.find((p) => p.roles.includes('homepage') && p.botVendor);
  const vendor = homepage?.botVendor;
  return vendor && vendor !== 'unknown' ? BOT_VENDOR_NAMES[vendor] : undefined;
}

const CONTACT_URL = `${process.env.REACT_APP_MARKETING_URL || 'https://getprivisy.com'}/contact`;

interface BlockedBySiteViewProps {
  result: ScanResult;
  onBack: () => void;
}

export default function BlockedBySiteView({ result, onBack }: BlockedBySiteViewProps) {
  const hostname = (() => {
    try {
      return new URL(result.url).hostname;
    } catch {
      return result.url;
    }
  })();
  const vendor = homepageBotVendor(result);
  const blockedBy = vendor ? `${vendor}'s firewall` : "The site's firewall";

  return (
    <div className="scan-body" style={{ minHeight: '100dvh', padding: 0 }}>
      <div className="scan-hero">
        <div className="scan-hero__nav">
          <button
            onClick={onBack}
            className="scan-hero__back-btn"
          >
            ← Back
          </button>
        </div>
        {/* Light document header (#1937). Ungraded, so no ring. */}
        <div className="scan-hero__body">
          <div className="report-column">
            <header className="report-header" data-testid="report-header">
              <p className="report-header__eyebrow">Privisy audit · {formatDate(result.startedAt)}</p>
              <div>
                <h1 className="report-header__verdict">Website Blocked Our Scanner</h1>
                <p className="report-header__domain">{hostname}</p>
              </div>
            </header>
            <div style={{ padding: '24px 0 0' }}>
              <p
                style={{
                  fontFamily: DT.fontBody,
                  fontSize: 15,
                  color: T.textMuted,
                  lineHeight: 1.6,
                  marginBottom: 24,
                }}
              >
                {blockedBy} blocked our California scanner, so we could not load the real page
                on <strong style={{ color: T.text }}>{hostname}</strong>. Instead of showing
                misleading compliance results, we stopped the scan.
              </p>
              {result.errors.length > 0 && (
                <div
                  style={{
                    backgroundColor: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    padding: '12px 16px',
                    textAlign: 'left',
                    marginBottom: 24,
                  }}
                >
                  <p style={{ fontFamily: DT.fontMono, fontSize: 12, color: T.textMuted, margin: 0 }}>
                    {result.errors[0]}
                  </p>
                </div>
              )}
              <p
                style={{
                  fontFamily: DT.fontBody,
                  fontSize: 14,
                  color: T.textMuted,
                  lineHeight: 1.5,
                }}
              >
                This is common for large sites with aggressive bot protection (e.g.&nbsp;Akamai,
                Cloudflare, PerimeterX). It does not reflect the site&apos;s actual privacy compliance.
                If this is your site,{' '}
                <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" style={{ color: T.accent }}>
                  contact us
                </a>{' '}
                and we&apos;ll help you allowlist our scanner.
              </p>
              <button onClick={onBack} className="legacy-cta__button">
                Scan another site
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
